import React, { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { PageLayout } from 'common/components';
import * as partnershipApi from 'client/partnership/api';
import { loadIBDraft, saveIBDraft } from './ibDraftStorage';
import { staticStyles } from './style';

const PRODUCTS_OPTIONS = [
  'Forex',
  'Stocks',
  'CFD',
  'Cryptocurrency CFD',
  'Cryptocurrency spot',
  'Metals',
  'Commodities',
  'Index',
];

const CLIENTS_OPTIONS = ['1-10', '11-25', '26-35', '36 or above'];
const VOLUME_OPTIONS = ['1-100', '101-300', '301-500', '501 or above'];
const DEPOSITS_OPTIONS = ['1-10,000', '10,001-50,000', '50,001-100,000', '100,001-300,000', '300,001 or above'];
const DURATION_OPTIONS = ['0-3 years', '4-5 years', '5-10 years', '10 years above'];
const REFERRAL_OPTIONS = ['Google', 'Friends', 'Sales team', 'Newspapers', 'Social media'];

const buildQuestionnaireData = ({
  q1,
  q1Duration,
  q1Brokers,
  q2,
  q3,
  q4,
  q5,
  q6,
  q7,
  q7Website,
  q8,
  q8Sales,
  attachments,
}) => {
  const data = {};
  const q1Data = { answer: q1 };
  if (q1 === 'Yes' && (q1Duration || q1Brokers)) {
    q1Data.extra = {};
    if (q1Duration) q1Data.extra.Duration = q1Duration;
    if (q1Brokers) q1Data.extra['Worked Brokers'] = q1Brokers;
  }
  data['IB Experience'] = q1Data;
  data['Interested Products'] = { answer: q2.join(',') };
  data['Target Markets'] = { answer: q3 };
  data['Expected Clients / Mo'] = { answer: q4 };
  data['Expected Volume'] = { answer: q5 };
  data['Expected Deposits'] = { answer: q6 };
  const q7Data = { answer: q7 };
  if (q7 === 'Yes' && q7Website) q7Data.extra = { '': q7Website };
  data['Promotion Website'] = q7Data;
  const q8Data = { answer: q8 };
  if (q8 === 'Sales team' && q8Sales) q8Data.extra = { 'Sales Person': q8Sales };
  data['Referral Source'] = q8Data;
  data.attachments = attachments.map(file => file.name);
  return data;
};

const restoreQuestionnaireData = (description, setters) => {
  if (!description || typeof description !== 'object') return;
  const experience = description['IB Experience'] || {};
  const products = description['Interested Products'] || {};
  const promotion = description['Promotion Website'] || {};
  const referral = description['Referral Source'] || {};
  setters.setQ1(experience.answer || '');
  setters.setQ1Duration((experience.extra && experience.extra.Duration) || '');
  setters.setQ1Brokers((experience.extra && experience.extra['Worked Brokers']) || '');
  setters.setQ2(products.answer ? products.answer.split(',').filter(Boolean) : []);
  setters.setQ3((description['Target Markets'] || {}).answer || '');
  setters.setQ4((description['Expected Clients / Mo'] || {}).answer || '');
  setters.setQ5((description['Expected Volume'] || {}).answer || '');
  setters.setQ6((description['Expected Deposits'] || {}).answer || '');
  setters.setQ7(promotion.answer || '');
  setters.setQ7Website((promotion.extra && promotion.extra['']) || '');
  setters.setQ8(referral.answer || '');
  setters.setQ8Sales((referral.extra && referral.extra['Sales Person']) || '');
};

const BecomeIntroducingBrokerPage = ({ profile }) => {
  const fileInputRef = useRef(null);
  const history = useHistory();
  const userId = profile.id;

  // Form state
  const [q1, setQ1] = useState(''); // IB Experience: Yes/No
  const [q1Duration, setQ1Duration] = useState(''); // Duration
  const [q1Brokers, setQ1Brokers] = useState(''); // Worked Brokers
  const [q2, setQ2] = useState([]); // Interested Products (multi-select)
  const [q3, setQ3] = useState(''); // Target Markets
  const [q4, setQ4] = useState(''); // Expected Clients / Mo
  const [q5, setQ5] = useState(''); // Expected Volume
  const [q6, setQ6] = useState(''); // Expected Deposits
  const [q7, setQ7] = useState(''); // Promotion Website: Yes/No
  const [q7Website, setQ7Website] = useState(''); // Website link
  const [q8, setQ8] = useState(''); // Referral Source
  const [q8Sales, setQ8Sales] = useState(''); // Sales Person
  const [attachments, setAttachments] = useState([]);
  const [supportAgentLanguageOptions, setSupportAgentLanguageOptions] = useState([]);
  const [language, setLanguage] = useState('en');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    axios.get('/support/languages/').then(({ data: { languages } }) => {
      // Only show English, filter out other languages
      const englishOnly = languages.filter(l => l.value === 'en');
      setSupportAgentLanguageOptions(englishOnly);
      if (englishOnly.length > 0) {
        setLanguage(englishOnly[0].value);
      }
    });
  }, []);

  useEffect(() => {
    let mounted = true;
    setQ1('');
    setQ1Duration('');
    setQ1Brokers('');
    setQ2([]);
    setQ3('');
    setQ4('');
    setQ5('');
    setQ6('');
    setQ7('');
    setQ7Website('');
    setQ8('');
    setQ8Sales('');
    setAttachments([]);
    setLanguage('en');
    setErrors({});
    const setters = {
      setQ1,
      setQ1Duration,
      setQ1Brokers,
      setQ2,
      setQ3,
      setQ4,
      setQ5,
      setQ6,
      setQ7,
      setQ7Website,
      setQ8,
      setQ8Sales,
    };
    Promise.all([loadIBDraft(userId), partnershipApi.getIBApplicationDraft().catch(() => null)]).then(
      ([localDraft, serverDraft]) => {
        if (!mounted) return;
        const draft = localDraft || (serverDraft && serverDraft.data);
        if (draft) {
          restoreQuestionnaireData(draft.description, setters);
          if (draft.language) setLanguage(draft.language);
          if (Array.isArray(draft.attachments)) setAttachments(draft.attachments);
        }
      }
    );
    return () => {
      mounted = false;
    };
  }, [userId]);

  // Validation
  const validate = useCallback(() => {
    const newErrors = {};
    if (!q1) newErrors.q1 = true;
    if (q2.length === 0) newErrors.q2 = true;
    if (!q4) newErrors.q4 = true;
    if (!q5) newErrors.q5 = true;
    if (!q6) newErrors.q6 = true;
    if (!q7) newErrors.q7 = true;
    if (!q8) newErrors.q8 = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [q1, q2, q4, q5, q6, q7, q8]);

  // Multi-select toggle for Q2
  const toggleProduct = useCallback(product => {
    setQ2(prev => (prev.includes(product) ? prev.filter(p => p !== product) : [...prev, product]));
  }, []);

  // File handling
  const handleFileSelect = useCallback(e => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(f => ['image/jpeg', 'image/png'].includes(f.type));
    setAttachments(prev => [...prev, ...validFiles]);
  }, []);

  const handleDrop = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files || []);
    const validFiles = files.filter(f => ['image/jpeg', 'image/png'].includes(f.type));
    setAttachments(prev => [...prev, ...validFiles]);
  }, []);

  const handleDragOver = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const removeAttachment = useCallback(index => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleDropzoneKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (fileInputRef.current) fileInputRef.current.click();
      }
    },
    [fileInputRef]
  );

  const questionnaireData = useCallback(
    () =>
      buildQuestionnaireData({
        q1,
        q1Duration,
        q1Brokers,
        q2,
        q3,
        q4,
        q5,
        q6,
        q7,
        q7Website,
        q8,
        q8Sales,
        attachments,
      }),
    [q1, q1Duration, q1Brokers, q2, q3, q4, q5, q6, q7, q7Website, q8, q8Sales, attachments]
  );

  const handleNextToContract = useCallback(async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    const data = questionnaireData();
    try {
      await saveIBDraft({ userId, description: data, language, attachments });
      await partnershipApi.saveIBApplicationDraft({ language, description: JSON.stringify(data) });
      history.push('/partnership/ib-contract');
    } catch (error) {
      console.error('Failed to save IB application draft:', error);
      setIsSubmitting(false);
      alert('An error occurred while saving your application. Please try again later.');
    }
  }, [attachments, history, language, questionnaireData, userId, validate]);

  // Submit for the original non-IB application path.
  const handleSubmit = useCallback(async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    const data = questionnaireData();
    const descriptionJSON = JSON.stringify(data, null, 2);

    try {
      await partnershipApi.submitIBApplication({
        language,
        description: descriptionJSON,
        attachments,
      });

      window.location.href = '/partnership';
    } catch (error) {
      console.error('Failed to submit partnership application:', error);
      setIsSubmitting(false);
      alert('An error occurred while submitting your application. Please try again later.');
    }
  }, [attachments, language, questionnaireData, validate]);

  return (
    <PageLayout externalClass="IBPageLayout">
      <div className="IBForm">
        <h1 className="IBForm__title">Become our Introducing Broker (IB)</h1>
        <p className="IBForm__subtitle">
          Please complete the questionnaire below to submit your partnership application.
        </p>

        <div className="IBForm__divider" />

        {/* Support agent language */}
        <div className="IBForm__section">
          <label className="IBForm__label">Support agent language</label>
          <div className="IBForm__select-wrapper">
            <select className="IBForm__select" value={language} onChange={e => setLanguage(e.target.value)}>
              {supportAgentLanguageOptions.length > 0 ? (
                supportAgentLanguageOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))
              ) : (
                <option value="en">English</option>
              )}
            </select>
          </div>
        </div>

        {/* Q1 - IB Experience */}
        <div className="IBForm__section">
          <p className="IBForm__question">1. Do you have experience as an IB (Introducing broker)?</p>
          <div className="IBForm__radio-group">
            <label className="IBForm__radio-label">
              <input
                type="radio"
                name="q1"
                value="No"
                checked={q1 === 'No'}
                onChange={() => setQ1('No')}
                className="IBForm__radio-input"
              />
              <span className="IBForm__radio-custom" />
              <span>No</span>
            </label>
            <label className="IBForm__radio-label">
              <input
                type="radio"
                name="q1"
                value="Yes"
                checked={q1 === 'Yes'}
                onChange={() => setQ1('Yes')}
                className="IBForm__radio-input"
              />
              <span className="IBForm__radio-custom" />
              <span>Yes</span>
            </label>
          </div>
          {errors.q1 && <p className="IBForm__error">This field is required</p>}

          {/* Sub-questions for Q1 */}
          {q1 === 'Yes' && (
            <div className="IBForm__sub-section">
              <p className="IBForm__sub-question">1-1. How many years?</p>
              <div className="IBForm__radio-group">
                {DURATION_OPTIONS.map(opt => (
                  <label key={opt} className="IBForm__radio-label">
                    <input
                      type="radio"
                      name="q1Duration"
                      value={opt}
                      checked={q1Duration === opt}
                      onChange={() => setQ1Duration(opt)}
                      className="IBForm__radio-input"
                    />
                    <span className="IBForm__radio-custom" />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>

              <p className="IBForm__sub-question" style={{ marginTop: 20 }}>
                1-2. Please mention the broker you have worked with:
              </p>
              <input
                type="text"
                className="IBForm__text-input"
                placeholder="e.g. Exness, XM..."
                value={q1Brokers}
                onChange={e => setQ1Brokers(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Q2 - Interested Products (Multi-select) */}
        <div className="IBForm__section">
          <p className="IBForm__question">
            2. Please choose the products you are interested in? <span className="IBForm__hint">(Multi-select)</span>
          </p>
          <div className="IBForm__checkbox-grid">
            {PRODUCTS_OPTIONS.map(product => (
              <label key={product} className="IBForm__checkbox-label">
                <input
                  type="checkbox"
                  checked={q2.includes(product)}
                  onChange={() => toggleProduct(product)}
                  className="IBForm__checkbox-input"
                />
                <span className="IBForm__checkbox-custom" />
                <span>{product}</span>
              </label>
            ))}
          </div>
          {errors.q2 && <p className="IBForm__error">This field is required</p>}
        </div>

        {/* Q3 - Target Markets */}
        <div className="IBForm__section">
          <p className="IBForm__question">
            3. Which are the countries or cities you are targeting for? <span className="IBForm__hint">(Optional)</span>
          </p>
          <input
            type="text"
            className="IBForm__text-input"
            placeholder="e.g. China, Southeast Asia, London..."
            value={q3}
            onChange={e => setQ3(e.target.value)}
          />
        </div>

        {/* Q4 - Expected Clients */}
        <div className="IBForm__section">
          <p className="IBForm__question">4. Number of clients you can generate per month?</p>
          <div className="IBForm__radio-group">
            {CLIENTS_OPTIONS.map(opt => (
              <label key={opt} className="IBForm__radio-label">
                <input
                  type="radio"
                  name="q4"
                  value={opt}
                  checked={q4 === opt}
                  onChange={() => setQ4(opt)}
                  className="IBForm__radio-input"
                />
                <span className="IBForm__radio-custom" />
                <span>{opt}</span>
              </label>
            ))}
          </div>
          {errors.q4 && <p className="IBForm__error">This field is required</p>}
        </div>

        {/* Q5 - Expected Volume */}
        <div className="IBForm__section">
          <p className="IBForm__question">5. What will be the monthly trading turnover (lots) expected?</p>
          <div className="IBForm__radio-group">
            {VOLUME_OPTIONS.map(opt => (
              <label key={opt} className="IBForm__radio-label">
                <input
                  type="radio"
                  name="q5"
                  value={opt}
                  checked={q5 === opt}
                  onChange={() => setQ5(opt)}
                  className="IBForm__radio-input"
                />
                <span className="IBForm__radio-custom" />
                <span>{opt}</span>
              </label>
            ))}
          </div>
          {errors.q5 && <p className="IBForm__error">This field is required</p>}
        </div>

        {/* Q6 - Expected Deposits */}
        <div className="IBForm__section">
          <p className="IBForm__question">6. Monthly deposits expected? (USD)</p>
          <div className="IBForm__radio-group">
            {DEPOSITS_OPTIONS.map(opt => (
              <label key={opt} className="IBForm__radio-label">
                <input
                  type="radio"
                  name="q6"
                  value={opt}
                  checked={q6 === opt}
                  onChange={() => setQ6(opt)}
                  className="IBForm__radio-input"
                />
                <span className="IBForm__radio-custom" />
                <span>{opt}</span>
              </label>
            ))}
          </div>
          {errors.q6 && <p className="IBForm__error">This field is required</p>}
        </div>

        {/* Q7 - Promotion Website */}
        <div className="IBForm__section">
          <p className="IBForm__question">
            7. Do you have any website for Promotion or individual IB? If yes, please mention.
          </p>
          <div className="IBForm__radio-group">
            <label className="IBForm__radio-label">
              <input
                type="radio"
                name="q7"
                value="No"
                checked={q7 === 'No'}
                onChange={() => setQ7('No')}
                className="IBForm__radio-input"
              />
              <span className="IBForm__radio-custom" />
              <span>No</span>
            </label>
            <label className="IBForm__radio-label">
              <input
                type="radio"
                name="q7"
                value="Yes"
                checked={q7 === 'Yes'}
                onChange={() => setQ7('Yes')}
                className="IBForm__radio-input"
              />
              <span className="IBForm__radio-custom" />
              <span>Yes</span>
            </label>
          </div>
          {errors.q7 && <p className="IBForm__error">This field is required</p>}

          {q7 === 'Yes' && (
            <div className="IBForm__sub-section">
              <p className="IBForm__sub-question">7-1. Please provide your website link or channel details:</p>
              <input
                type="text"
                className="IBForm__text-input"
                placeholder="e.g. www.myibsite.com"
                value={q7Website}
                onChange={e => setQ7Website(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Q8 - Referral Source */}
        <div className="IBForm__section">
          <p className="IBForm__question">8. Where did you hear about us?</p>
          <div className="IBForm__radio-group">
            {REFERRAL_OPTIONS.map(opt => (
              <label key={opt} className="IBForm__radio-label">
                <input
                  type="radio"
                  name="q8"
                  value={opt}
                  checked={q8 === opt}
                  onChange={() => setQ8(opt)}
                  className="IBForm__radio-input"
                />
                <span className="IBForm__radio-custom" />
                <span>{opt}</span>
              </label>
            ))}
          </div>
          {errors.q8 && <p className="IBForm__error">This field is required</p>}

          {q8 === 'Sales team' && (
            <div className="IBForm__sub-section">
              <p className="IBForm__sub-question">8-1. Please mention the name of Sales person:</p>
              <input
                type="text"
                className="IBForm__text-input"
                placeholder="e.g. John"
                value={q8Sales}
                onChange={e => setQ8Sales(e.target.value)}
              />
            </div>
          )}
        </div>

        {/* Attachments */}
        <div className="IBForm__section">
          <p className="IBForm__question">
            Attachments <span className="IBForm__hint">(Optional)</span>
          </p>
          <div
            className="IBForm__dropzone"
            role="button"
            tabIndex={0}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            onKeyDown={handleDropzoneKeyDown}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <p className="IBForm__dropzone-text">
              <span className="IBForm__dropzone-link">+ Drop your attachments here</span> (JPG, PNG)
            </p>
          </div>
          {attachments.length > 0 && (
            <div className="IBForm__attachment-list">
              {attachments.map((file, index) => (
                <div key={index} className="IBForm__attachment-item">
                  <span className="IBForm__attachment-name">{file.name}</span>
                  <button type="button" className="IBForm__attachment-remove" onClick={() => removeAttachment(index)}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="IBForm__divider" />

        {/* Submit Button */}
        <div className={`IBForm__submit-wrapper${profile.isIBInvite ? ' IBForm__submit-wrapper--contract' : ''}`}>
          {profile.isIBInvite ? (
            <button type="button" className="IBForm__submit-btn" onClick={handleNextToContract} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Next: Sign Contract'}
            </button>
          ) : (
            <button type="button" className="IBForm__submit-btn" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </button>
          )}
        </div>
      </div>

      <style jsx>{staticStyles}</style>
    </PageLayout>
  );
};

export { BecomeIntroducingBrokerPage };

BecomeIntroducingBrokerPage.propTypes = {
  profile: PropTypes.object,
};

BecomeIntroducingBrokerPage.defaultProps = {
  profile: {},
};

export default connect(state => ({ profile: state.user.profile || {} }))(BecomeIntroducingBrokerPage);
